// Admin tenants API — typed wrappers around /admin/v1/tenants/*. Mirrors
// the shape used by apps/dashboard/src/lib/api/* (one query-key constant
// per resource + a function that calls the api client).

import type { ApiClient } from '../api-client.js';

export type TenantPlanFilter = 'all' | 'free' | 'starter' | 'pro';
export type TenantFreezeFilter = 'all' | 'frozen' | 'active';
export type TenantLifecycleFilter = 'all' | 'live' | 'soft_deleted';

export interface TenantRow {
  id: string;
  name: string;
  email: string | null;
  createdAt: string;
  deletedAt: string | null;
  purgeAfter: string | null;
  planCode: string | null;
  subscriptionStatus: string | null;
  frozenAt: string | null;
  frozenReason: string | null;
  costLast30dUsd: number;
  lastActiveAt: string | null;
}

export interface TenantDetail extends TenantRow {
  agentCount: number;
  apiKeyCount: number;
  conversationCount: number;
  ticketCount: number;
  kbDocCount: number;
  kbBytes: number;
}

export interface ListTenantsParams {
  search?: string;
  plan?: TenantPlanFilter;
  freeze?: TenantFreezeFilter;
  lifecycle?: TenantLifecycleFilter;
  limit?: number;
}

export const TENANTS_LIST_QUERY_KEY = ['admin', 'tenants'] as const;
export const tenantQueryKey = (id: string): readonly unknown[] =>
  ['admin', 'tenants', id] as const;

function buildQuery(params: ListTenantsParams): string {
  const sp = new URLSearchParams();
  if (params.search && params.search.trim().length > 0) {
    sp.set('search', params.search.trim());
  }
  if (params.plan && params.plan !== 'all') sp.set('plan', params.plan);
  if (params.freeze && params.freeze !== 'all') sp.set('freeze', params.freeze);
  if (params.lifecycle && params.lifecycle !== 'live') sp.set('lifecycle', params.lifecycle);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  const tail = sp.toString();
  return tail.length > 0 ? `?${tail}` : '';
}

export const tenantsApi = {
  async list(client: ApiClient, params: ListTenantsParams = {}): Promise<{ rows: TenantRow[] }> {
    return client.get<{ rows: TenantRow[] }>(`/tenants${buildQuery(params)}`);
  },
  async detail(client: ApiClient, id: string): Promise<TenantDetail> {
    return client.get<TenantDetail>(`/tenants/${id}`);
  },
  async freeze(client: ApiClient, id: string, reason: string): Promise<{ tenantId: string }> {
    return client.post<{ tenantId: string }>(`/tenants/${id}/freeze`, { reason });
  },
  async unfreeze(client: ApiClient, id: string): Promise<{ tenantId: string }> {
    return client.post<{ tenantId: string }>(`/tenants/${id}/unfreeze`, {});
  },
  async restore(client: ApiClient, id: string): Promise<{ tenantId: string }> {
    return client.post<{ tenantId: string }>(`/tenants/${id}/restore`, {});
  },
};
